/**
 * CONFIGURAÇÃO CENTRAL DE PERMISSÕES (RBAC)
 * 
 * Define os papéis (Roles) e as capacidades (Capabilities) de cada um.
 * Esta é a FONTE ÚNICA DE VERDADE para frontend e backend (via regras geradas).
 */

// 1. Definição de Papéis (Devem bater com o banco de dados)
export const ROLES = {
    ADMIN: 'admin',
    DIRETORIA: 'diretoria',
    SECRETARIA: 'secretaria', // ou 'secretária' (precisamos padronizar)
    AUDITOR_QUALIDADE: 'auditor_qualidade',
    GESTOR_PRONTOBABY: 'gestor_prontobaby',
    GESTOR_HOMECARE: 'gestor_homecare',
    COORDENADOR: 'coordenador',
    COLABORADOR: 'colaborador',
    COMENTADOR: 'comentador', // Legado? Verificar se ainda uso
    COOPERATIVA: 'cooperativa',
    ESCALISTA: 'escalista',
    RH: 'rh',
    TI: 'ti',
    SUPERVISOR_NUCLEO: 'supervisor_nucleo',
    ASSISTENTE: 'assistente'
};

// 2. Definição de Capacidades (O que pode ser feito)
export const CAPABILITIES = {
    // GERAL
    VIEW_DASHBOARD: 'view_dashboard',
    MANAGE_SETTINGS: 'manage_settings',

    // AGENDA DIREÇÃO
    VIEW_AGENDA_DIRECAO: 'view_agenda_direcao',
    EDIT_AGENDA_DIRECAO_ALL: 'edit_agenda_direcao_all', // Secretária/Admin
    EDIT_AGENDA_DIRECAO_OWN: 'edit_agenda_direcao_own', // Diretor editando sua própria

    // PROJETO DIREÇÃO
    VIEW_PROJETO_DIRECAO: 'view_projeto_direcao',
    EDIT_PROJETO_DIRECAO: 'edit_projeto_direcao',
    CREATE_TASK_DIRECAO: 'create_task_direcao',

    // OBRAS DIREÇÃO
    VIEW_OBRAS_DIRECAO: 'view_obras_direcao',
    EDIT_OBRAS_DIRECAO: 'edit_obras_direcao',

    // AGENDA QUALIDADE
    VIEW_AGENDA_QUALIDADE: 'view_agenda_qualidade',
    EDIT_AGENDA_QUALIDADE: 'edit_agenda_qualidade',

    // AGENDA HOMECARE
    VIEW_AGENDA_HOMECARE: 'view_agenda_homecare',
    EDIT_AGENDA_HOMECARE: 'edit_agenda_homecare',

    // PROJETO HOMECARE
    VIEW_PROJETO_HOMECARE: 'view_projeto_homecare',
    EDIT_PROJETO_HOMECARE: 'edit_projeto_homecare',

    // CHAMADOS
    OPEN_TICKET: 'open_ticket',
    VIEW_ALL_TICKETS: 'view_all_tickets',
    MANAGE_TICKETS: 'manage_tickets', // Técnico/TI

    // NOVOS PORTAIS (FASE 5)
    VIEW_PORTAL_COOPERATIVA: 'view_portal_cooperativa',
    VIEW_GESTAO_ESCALAS: 'view_gestao_escalas',
    VIEW_ESCALAS_ENFERMEIROS: 'view_escalas_enfermeiros',
    VIEW_AVALIACAO_RH: 'view_avaliacao_rh',
    VIEW_ACESSOS_TI: 'view_acessos_ti',
    VIEW_FORMULARIO_PASSAGEM: 'view_formulario_passagem',
    VIEW_GESTAO_PASSAGENS: 'view_gestao_passagens',
    VIEW_GESTAO_PACIENTES: 'view_gestao_pacientes',

    // ESTOQUE (FASE 6)
    VIEW_ESTOQUE_CONTROLE: 'view_estoque_controle',
    VIEW_ESTOQUE_SOLICITACAO: 'view_estoque_solicitacao',
    VIEW_ESTOQUE_CONSUMO: 'view_estoque_consumo',

    // EXTRAS / LEGADOS / NOVOS
    VIEW_PROJETO_IW: 'view_projeto_iw',
    VIEW_ANALYSIS: 'view_analysis',
    VIEW_TAREFAS_HOMECARE: 'view_tarefas_homecare',
    VIEW_TAREFAS_QUALIDADE: 'view_tarefas_qualidade',
    VIEW_INDICADORES: 'view_indicadores',
    VIEW_ABRIR_CHAMADO: 'view_abrir_chamado',
    VIEW_MEUS_CHAMADOS: 'view_meus_chamados',
    VIEW_PAINEL_CHAMADOS: 'view_painel_chamados',
    VIEW_ADMIN_TUTORIAIS: 'view_admin_tutoriais',
    VIEW_CONFIGURACOES: 'view_configuracoes',
    VIEW_HTML_VIEWER: 'view_html_viewer',
    VIEW_AULAS: 'view_aulas',
    VIEW_CONVITES: 'view_convites',
    VIEW_MEU_PROJETO: 'view_meu_projeto',
    VIEW_MINHAS_TAREFAS: 'view_minhas_tarefas',

    // OCORRÊNCIAS QUALITY (Fase 7)
    VIEW_OCORRENCIAS_SUPERVISOR: 'view_ocorrencias_supervisor',
    VIEW_OCORRENCIAS_SETORES: 'view_ocorrencias_setores',
    EDIT_OCORRENCIAS: 'edit_ocorrencias',

    // GESTÃO DE OCORRÊNCIAS V2 (Fase 8)
    VIEW_GESTAO_OCORRENCIAS: 'view_gestao_ocorrencias',
    CREATE_OCORRENCIA: 'create_ocorrencia',
};

// 3. Matriz de Permissões (Quem pode fazer o quê)
export const ROLE_PERMISSIONS = {
    [ROLES.ADMIN]: ['*'], // Superusuário (faz tudo)

    [ROLES.DIRETORIA]: [
        CAPABILITIES.VIEW_DASHBOARD,
        CAPABILITIES.VIEW_AGENDA_DIRECAO,
        CAPABILITIES.EDIT_AGENDA_DIRECAO_OWN,
        CAPABILITIES.VIEW_PROJETO_DIRECAO,
        CAPABILITIES.EDIT_PROJETO_DIRECAO,
        CAPABILITIES.CREATE_TASK_DIRECAO,
        CAPABILITIES.VIEW_OBRAS_DIRECAO,
        CAPABILITIES.EDIT_OBRAS_DIRECAO,
        CAPABILITIES.OPEN_TICKET,
        CAPABILITIES.VIEW_AGENDA_QUALIDADE // Leitura apenas?
    ],

    [ROLES.SECRETARIA]: [
        CAPABILITIES.VIEW_DASHBOARD,
        CAPABILITIES.VIEW_AGENDA_DIRECAO,
        CAPABILITIES.EDIT_AGENDA_DIRECAO_ALL,
        CAPABILITIES.VIEW_PROJETO_DIRECAO,
        CAPABILITIES.EDIT_PROJETO_DIRECAO,
        CAPABILITIES.CREATE_TASK_DIRECAO,
        CAPABILITIES.VIEW_OBRAS_DIRECAO,
        CAPABILITIES.EDIT_OBRAS_DIRECAO,
        CAPABILITIES.OPEN_TICKET
    ],

    [ROLES.AUDITOR_QUALIDADE]: [
        CAPABILITIES.VIEW_DASHBOARD,
        CAPABILITIES.VIEW_AGENDA_QUALIDADE,
        CAPABILITIES.EDIT_AGENDA_QUALIDADE,
        CAPABILITIES.OPEN_TICKET
    ],

    [ROLES.GESTOR_HOMECARE]: [
        CAPABILITIES.VIEW_DASHBOARD,
        CAPABILITIES.VIEW_AGENDA_HOMECARE,
        CAPABILITIES.EDIT_AGENDA_HOMECARE,
        CAPABILITIES.OPEN_TICKET,
        CAPABILITIES.VIEW_ADMIN_TUTORIAIS
    ],

    [ROLES.COOPERATIVA]: [
        CAPABILITIES.VIEW_PORTAL_COOPERATIVA,
        CAPABILITIES.VIEW_GESTAO_ESCALAS,
        CAPABILITIES.VIEW_ESCALAS_ENFERMEIROS
    ],

    [ROLES.ESCALISTA]: [
        CAPABILITIES.VIEW_GESTAO_ESCALAS,
        CAPABILITIES.VIEW_ESCALAS_ENFERMEIROS,
        CAPABILITIES.VIEW_FORMULARIO_PASSAGEM,
        CAPABILITIES.VIEW_GESTAO_PASSAGENS,
        CAPABILITIES.VIEW_GESTAO_PACIENTES
    ],

    [ROLES.RH]: [
        CAPABILITIES.VIEW_AVALIACAO_RH
    ],

    [ROLES.TI]: [
        CAPABILITIES.VIEW_ACESSOS_TI
    ],

    [ROLES.SUPERVISOR_NUCLEO]: [
        CAPABILITIES.VIEW_DASHBOARD,
        CAPABILITIES.VIEW_OCORRENCIAS_SUPERVISOR,
        CAPABILITIES.VIEW_OCORRENCIAS_SETORES,
        CAPABILITIES.EDIT_OCORRENCIAS,
        CAPABILITIES.VIEW_AGENDA_QUALIDADE,
        CAPABILITIES.OPEN_TICKET,
        CAPABILITIES.VIEW_ADMIN_TUTORIAIS
    ],

    [ROLES.AUDITOR_QUALIDADE]: [
        CAPABILITIES.VIEW_DASHBOARD,
        CAPABILITIES.VIEW_AGENDA_QUALIDADE,
        CAPABILITIES.EDIT_AGENDA_QUALIDADE,
        CAPABILITIES.VIEW_OCORRENCIAS_SETORES,
        CAPABILITIES.OPEN_TICKET
    ],

    [ROLES.ASSISTENTE]: [
        CAPABILITIES.VIEW_GESTAO_OCORRENCIAS,
        CAPABILITIES.CREATE_OCORRENCIA,
        CAPABILITIES.OPEN_TICKET
    ],
};

// 4. Mapeamento de Capacidades para Permissões Granulares (Settings.js)
// Formato: CAPABILITY: { module: 'id_modulo', action: 'view' | 'edit' }
const GRANULAR_MAPPINGS = {
    [CAPABILITIES.VIEW_AGENDA_DIRECAO]: { module: 'agenda_direcao', action: 'view' },
    [CAPABILITIES.EDIT_AGENDA_DIRECAO_ALL]: { module: 'agenda_direcao', action: 'edit' },
    [CAPABILITIES.VIEW_PROJETO_DIRECAO]: { module: 'projeto_direcao', action: 'view' },
    [CAPABILITIES.EDIT_PROJETO_DIRECAO]: { module: 'projeto_direcao', action: 'edit' },
    [CAPABILITIES.VIEW_AGENDA_QUALIDADE]: { module: 'agenda_qualidade', action: 'view' },
    [CAPABILITIES.EDIT_AGENDA_QUALIDADE]: { module: 'agenda_qualidade', action: 'edit' },
    [CAPABILITIES.VIEW_AGENDA_HOMECARE]: { module: 'agenda_homecare', action: 'view' },
    [CAPABILITIES.EDIT_AGENDA_HOMECARE]: { module: 'agenda_homecare', action: 'edit' },
    [CAPABILITIES.VIEW_OBRAS_DIRECAO]: { module: 'obras_direcao', action: 'view' },
    [CAPABILITIES.EDIT_OBRAS_DIRECAO]: { module: 'obras_direcao', action: 'edit' },
    [CAPABILITIES.VIEW_PROJETO_HOMECARE]: { module: 'projeto_homecare', action: 'view' },
    [CAPABILITIES.EDIT_PROJETO_HOMECARE]: { module: 'projeto_homecare', action: 'edit' },
    
    // Novos módulos (Fase 5)
    [CAPABILITIES.VIEW_PORTAL_COOPERATIVA]: { module: 'portal_cooperativa', action: 'view' },
    [CAPABILITIES.VIEW_GESTAO_ESCALAS]: { module: 'gestao_escalas', action: 'view' },
    [CAPABILITIES.VIEW_ESCALAS_ENFERMEIROS]: { module: 'escalas_enfermeiros', action: 'view' },
    [CAPABILITIES.VIEW_AVALIACAO_RH]: { module: 'avaliacao_rh', action: 'view' },
    [CAPABILITIES.VIEW_ACESSOS_TI]: { module: 'acessos_ti', action: 'view' },
    [CAPABILITIES.VIEW_FORMULARIO_PASSAGEM]: { module: 'formulario_passagem', action: 'view' },
    [CAPABILITIES.VIEW_GESTAO_PASSAGENS]: { module: 'gestao_passagens', action: 'view' },
    [CAPABILITIES.VIEW_GESTAO_PACIENTES]: { module: 'gestao_pacientes', action: 'view' },
    
    // Estoque (Fase 6)
    [CAPABILITIES.VIEW_ESTOQUE_CONTROLE]: { module: 'estoque_controle', action: 'view' },
    [CAPABILITIES.VIEW_ESTOQUE_SOLICITACAO]: { module: 'estoque_solicitacao', action: 'view' },
    [CAPABILITIES.VIEW_ESTOQUE_CONSUMO]: { module: 'estoque_consumo', action: 'view' },

    // Mapeamentos Adicionais
    [CAPABILITIES.VIEW_PROJETO_IW]: { module: 'projeto_iw', action: 'view' },
    [CAPABILITIES.VIEW_ANALYSIS]: { module: 'analysis', action: 'view' },
    [CAPABILITIES.VIEW_TAREFAS_HOMECARE]: { module: 'tarefas_homecare', action: 'view' },
    [CAPABILITIES.VIEW_TAREFAS_QUALIDADE]: { module: 'tarefas_qualidade', action: 'view' },
    [CAPABILITIES.VIEW_INDICADORES]: { module: 'indicadores', action: 'view' },
    [CAPABILITIES.VIEW_ABRIR_CHAMADO]: { module: 'abrir_chamado', action: 'view' },
    [CAPABILITIES.VIEW_MEUS_CHAMADOS]: { module: 'meus_chamados', action: 'view' },
    [CAPABILITIES.VIEW_PAINEL_CHAMADOS]: { module: 'painel_chamados', action: 'view' },
    [CAPABILITIES.VIEW_ADMIN_TUTORIAIS]: { module: 'admin_tutoriais', action: 'view' },
    [CAPABILITIES.VIEW_CONFIGURACOES]: { module: 'configuracoes', action: 'view' },
    [CAPABILITIES.VIEW_HTML_VIEWER]: { module: 'html_viewer', action: 'view' },
    [CAPABILITIES.VIEW_AULAS]: { module: 'aulas', action: 'view' },
    [CAPABILITIES.VIEW_CONVITES]: { module: 'convites', action: 'view' },
    [CAPABILITIES.VIEW_MEU_PROJETO]: { module: 'meu_projeto', action: 'view' },
    [CAPABILITIES.VIEW_MINHAS_TAREFAS]: { module: 'minhas_tarefas', action: 'view' },

    // Ocorrências (Fase 7)
    [CAPABILITIES.VIEW_OCORRENCIAS_SUPERVISOR]: { module: 'ocorrencias_supervisor', action: 'view' },
    [CAPABILITIES.VIEW_OCORRENCIAS_SETORES]: { module: 'ocorrencias_setores', action: 'view' },
    [CAPABILITIES.EDIT_OCORRENCIAS]: { module: 'ocorrencias', action: 'edit' },

    // Gestão de Ocorrências V2 (Fase 8)
    [CAPABILITIES.VIEW_GESTAO_OCORRENCIAS]: { module: 'gestao_ocorrencias', action: 'view' },
    [CAPABILITIES.CREATE_OCORRENCIA]: { module: 'gestao_ocorrencias', action: 'edit' },
};

/**
 * Verifica se um usuário possui uma determinada capacidade/permissão.
 * @param {string|string[]} userRoles - Papel ou lista de papéis do usuário
 * @param {string} capability - Capacidade a verificar (ex: 'view_dashboard')
 * @param {Object} [userPermissions] - Objeto de permissões granulares do usuário (opcional)
 * @returns {boolean}
 */
export function hasPermission(userRoles, capability, userPermissions = null) {
    // 1. Verifica se usuário existe
    if (!userRoles && !userPermissions) return false;

    // Normaliza roles para array
    const roles = userRoles ? (Array.isArray(userRoles) ? userRoles : [userRoles]) : [];

    // 2. Verifica se é Admin (Acesso Total - Nível Supremo)
    if (roles.includes(ROLES.ADMIN) || roles.includes('administrador')) return true;

    // 3. PRIORIDADE: Verifica Permissões Granulares (Checkbox do Settings)
    // Se o módulo está mapeado e o usuário tem uma configuração explícita (true ou false), ela manda.
    if (userPermissions && GRANULAR_MAPPINGS[capability]) {
        const mapping = GRANULAR_MAPPINGS[capability];
        const modulePerms = userPermissions[mapping.module];
        
        // Se existe uma configuração explícita para este tipo de ação (view ou edit)
        if (modulePerms && typeof modulePerms[mapping.action] === 'boolean') {
            return modulePerms[mapping.action]; // Retorna true ou false conforme o checkbox
        }
    }

    // 4. FALLBACK: Verifica permissões padrão dos papéis (Roles)
    return roles.some(role => {
        const permissions = ROLE_PERMISSIONS[role];
        if (!permissions) return false;

        return permissions.includes(capability) || permissions.includes('*');
    });
}
